import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionButton,
  DataTable,
  GlassPanel,
  ModalSheet,
  SegmentedControl,
  UtilityButton,
} from "@guandan/ui";
import { cardFromId } from "../game/cards";
import {
  createPlatformService,
  type PlatformService,
} from "../lib/platform-api";
import {
  createPlayerDataClient,
  createRemoteReplayPresentation,
  deriveHistoryBehaviorRates,
  reportReasons,
  type AuthoritativeHistorySummary,
  type AuthoritativeMatchHistory,
  type ReportReason,
  type ReplayPresentation,
} from "../lib/player-data";
import { getSessionToken } from "../lib/session";
import {
  UserMusicPlayer,
  userMusicApi,
  type UserMusicTrack,
} from "../lib/user-music";
import { promptPwaInstall, watchInstallPrompt } from "../lib/pwa";
import type { AppSettings, MatchRecord, PlayerProfile } from "../lib/storage";
import { BottomNav, type LobbyRoute } from "./Lobby";
import { MUSIC_TRACKS } from "../game/game-sound";

function Header({
  title,
  subtitle,
  onBack,
}: {
  title: string;
  subtitle: string;
  onBack: () => void;
}) {
  return (
    <header className="sub-header">
      <div>
        <span>{subtitle}</span>
        <h1>{title}</h1>
      </div>
      <UtilityButton onClick={onBack}>返回大厅</UtilityButton>
    </header>
  );
}

export function Matchmaking({
  profile,
  onCancel,
  onMatched,
}: {
  profile: PlayerProfile;
  onCancel: () => void;
  onMatched: (roomId: string, roomCode?: string) => void;
}) {
  const service = useMemo(
    () => createPlatformService(profile),
    [profile.email, profile.nickname],
  );
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [seconds, setSeconds] = useState(0);
  const [people, setPeople] = useState(1);
  const [stage, setStage] = useState("正在加入匹配队列");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [retrySequence, setRetrySequence] = useState(0);
  const cancelled = useRef(false);
  const opened = useRef(false);
  const onMatchedRef = useRef(onMatched);
  onMatchedRef.current = onMatched;

  useEffect(() => {
    const ticker = window.setInterval(
      () => setSeconds((value) => value + 1),
      1000,
    );
    return () => window.clearInterval(ticker);
  }, []);

  useEffect(() => {
    let disposed = false;
    let timer = 0;
    cancelled.current = false;
    setError("");
    const shouldStop = () => cancelled.current || opened.current;

    const poll = async () => {
      if (disposed || shouldStop()) return;
      try {
        const entry = await service.matchStatus();
        if (shouldStop()) return;
        if (!entry) throw new Error("匹配队列状态不存在，请重新匹配");
        if (entry.status === "cancelled" || entry.status === "expired")
          throw new Error(
            entry.status === "cancelled"
              ? "匹配已取消"
              : "匹配已过期，请重新发起",
          );
        if (entry.status === "matched" && entry.matchedRoomId) {
          setStage(
            service.mode === "remote"
              ? "已匹配，正在确认准备并启动权威牌桌"
              : "本地席位已确认，正在由 AI 补齐四席",
          );
          if (service.mode === "local") {
            opened.current = true;
            onMatchedRef.current(entry.matchedRoomId);
            return;
          }
          const started = await prepareMatchedRoom(
            service,
            entry.matchedRoomId,
            setPeople,
          );
          if (shouldStop()) return;
          if (started.phase === "playing") {
            opened.current = true;
            onMatchedRef.current(entry.matchedRoomId, started.code);
            return;
          }
        } else {
          const humanSeconds = Math.ceil(entry.humanPriorityRemainingMs / 1000);
          const timeoutSeconds = Math.ceil(entry.timeoutRemainingMs / 1000);
          setStage(
            humanSeconds > 0
              ? `真人优先匹配 · ${humanSeconds} 秒后扩大补位范围`
              : timeoutSeconds > 0
                ? `正在扩大范围 · 最多 ${timeoutSeconds} 秒后 AI 补位`
                : "正在等待服务器确认 AI 补位",
          );
        }
        timer = window.setTimeout(() => void poll(), 1_000);
      } catch (cause) {
        if (!shouldStop())
          setError(
            cause instanceof Error ? cause.message : "匹配服务暂时不可用",
          );
      }
    };

    const enqueue = async () => {
      try {
        const entry = await service.enqueueMatch({
          mode: "casual",
          rating: profile.level * 100,
          idempotencyKey,
        });
        if (disposed || cancelled.current) return;
        setStage(
          entry.status === "matched"
            ? "已匹配，正在准备牌桌"
            : "已进入幂等匹配队列",
        );
        void poll();
      } catch (cause) {
        if (!disposed && !cancelled.current)
          setError(cause instanceof Error ? cause.message : "无法加入匹配队列");
      }
    };
    void enqueue();
    return () => {
      disposed = true;
      window.clearTimeout(timer);
    };
  }, [idempotencyKey, profile.level, retrySequence, service]);

  async function cancelMatch() {
    if (busy) return;
    cancelled.current = true;
    setBusy(true);
    setError("");
    try {
      await service.cancelMatch();
      onCancel();
    } catch (cause) {
      cancelled.current = false;
      setError(cause instanceof Error ? cause.message : "取消匹配失败，请重试");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="match-screen">
      <GlassPanel>
        <h1>{error ? "匹配暂时中断" : "匹配中…"}</h1>
        <p className="local-service-note">
          {service.mode === "remote"
            ? "联网权威匹配 · 服务器确认队列、取消与 AI 补位"
            : "本地可玩匹配 · 当前设备一名真人，真实 AI 自动补位"}
        </p>
        <p>
          {error || stage} · 已等待 {seconds} 秒
        </p>
        <div className="match-orbit">
          <span className="match-self">{profile.nickname}</span>
          {[1, 2, 3].map((seat) => (
            <span
              className={people > seat ? "matched" : "searching"}
              key={seat}
            >
              {people > seat
                ? "真人牌友"
                : service.mode === "local"
                  ? "AI 将补位"
                  : "匹配中"}
            </span>
          ))}
        </div>
        {error ? (
          <div className="button-row">
            <ActionButton
              tone="blue"
              onClick={() => {
                setError("");
                setRetrySequence((value) => value + 1);
              }}
            >
              重试匹配
            </ActionButton>
            <ActionButton tone="ghost" onClick={() => void cancelMatch()}>
              返回大厅
            </ActionButton>
          </div>
        ) : (
          <ActionButton
            tone="ghost"
            disabled={busy}
            onClick={() => void cancelMatch()}
          >
            {busy ? "正在取消…" : "取消匹配"}
          </ActionButton>
        )}
      </GlassPanel>
    </main>
  );
}

async function prepareMatchedRoom(
  service: PlatformService,
  roomId: string,
  setPeople: (count: number) => void,
) {
  let room = await service.refreshRoom(roomId);
  setPeople(room.members.length);
  if (room.phase === "playing") return room;
  if (!room.selfMember.ready) room = await service.setReady(room, true);
  setPeople(room.members.length);
  if (
    room.ownerId === service.identity.userId &&
    room.members.every((member) => member.ready)
  )
    room = await service.startRoom(room);
  return room;
}

export function RecordsScreen({
  matches,
  onBack,
  onNavigate,
}: {
  profile: PlayerProfile;
  matches: readonly MatchRecord[];
  onBack: () => void;
  onNavigate: (route: LobbyRoute) => void;
}) {
  const [client] = useState(() => createPlayerDataClient());
  const [retrySequence, setRetrySequence] = useState(0);
  const [remoteState, setRemoteState] = useState<RemoteHistoryState>(() =>
    client
      ? { status: "loading", message: "正在同步参与者权威战绩…" }
      : {
          status: "unavailable",
          message: "未连接联网战绩服务；下方显示本机权威牌局缓存。",
        },
  );
  const [replay, setReplay] = useState<ReplayPresentation | null>(null);
  const [replayLoadingId, setReplayLoadingId] = useState<string | null>(null);
  const [replayError, setReplayError] = useState("");
  const replayRequest = useRef<AbortController | null>(null);
  const localCachedMatches = useMemo(
    () =>
      matches.filter(
        (match) =>
          match.mode === "电脑练习" ||
          match.roomId.startsWith("local-friend-") ||
          match.roomId.startsWith("local-match-"),
      ),
    [matches],
  );

  useEffect(() => {
    if (!client) return;
    const controller = new AbortController();
    setRemoteState({ status: "loading", message: "正在同步参与者权威战绩…" });
    void client
      .history(controller.signal)
      .then((history) =>
        setRemoteState({
          status: "ready",
          message: "联网权威战绩 · 仅统计服务器已最终结算且本人参与的牌局",
          ...history,
        }),
      )
      .catch((cause: unknown) => {
        if (!controller.signal.aborted)
          setRemoteState({
            status: "error",
            message:
              cause instanceof Error ? cause.message : "联网战绩同步失败",
          });
      });
    return () => controller.abort();
  }, [client, retrySequence]);

  useEffect(() => () => replayRequest.current?.abort(), []);

  const remoteMatches =
    remoteState.status === "ready" ? remoteState.matches : [];
  const remoteSummary =
    remoteState.status === "ready" ? remoteState.summary : null;
  const wins = remoteMatches.filter((match) => match.metrics.won).length;
  const firstFinishes = remoteMatches.filter(
    (match) => match.finishOrder === 1,
  ).length;
  const doubleUps = remoteMatches.filter(
    (match) => match.metrics.doubleUp,
  ).length;
  const doubleDowns = remoteMatches.filter(
    (match) => match.metrics.doubleDown,
  ).length;
  const averagePlacement = remoteMatches.length
    ? remoteMatches.reduce((sum, match) => sum + match.finishOrder, 0) /
      remoteMatches.length
    : 0;
  const behaviorRates = deriveHistoryBehaviorRates(remoteMatches);

  async function loadRemoteReplay(row: AuthoritativeMatchHistory) {
    if (!client || replayLoadingId) return;
    replayRequest.current?.abort();
    const controller = new AbortController();
    replayRequest.current = controller;
    setReplayError("");
    setReplayLoadingId(row.matchId);
    try {
      const result = await client.replay(row.matchId, controller.signal);
      setReplay(createRemoteReplayPresentation(row, result));
    } catch (cause) {
      if (!controller.signal.aborted)
        setReplayError(
          cause instanceof Error ? cause.message : "权威回放读取失败",
        );
    } finally {
      if (!controller.signal.aborted) setReplayLoadingId(null);
    }
  }

  const remoteColumns = [
    {
      key: "time",
      title: "时间",
      render: (row: AuthoritativeMatchHistory) => formatMatchTime(row.endedAt),
    },
    { key: "mode", title: "来源", render: () => "联网权威对局" },
    {
      key: "result",
      title: "结果",
      render: (row: AuthoritativeMatchHistory) =>
        row.metrics.won ? "胜利" : "失败",
    },
    {
      key: "placement",
      title: "名次",
      render: (row: AuthoritativeMatchHistory) =>
        placementLabel(row.finishOrder),
    },
    {
      key: "level",
      title: "本局升级",
      render: (row: AuthoritativeMatchHistory) => signed(row.levelDelta),
    },
    {
      key: "score",
      title: "权威积分",
      render: (row: AuthoritativeMatchHistory) => signed(row.score),
    },
    {
      key: "plays",
      title: "出牌 / 炸弹",
      render: (row: AuthoritativeMatchHistory) =>
        `${row.metrics.plays} / ${row.metrics.bombsPlayed}`,
    },
    {
      key: "rights",
      title: "获得牌权",
      render: (row: AuthoritativeMatchHistory) => row.metrics.trickRights,
    },
    {
      key: "trustee",
      title: "托管动作 / 时长 / 断线",
      render: (row: AuthoritativeMatchHistory) =>
        `${row.metrics.managedTurns} / ${row.metrics.trusteeSeconds}s / ${row.metrics.disconnects}`,
    },
    {
      key: "action",
      title: "回放",
      render: (row: AuthoritativeMatchHistory) => (
        <button
          className="table-link"
          type="button"
          disabled={replayLoadingId !== null}
          onClick={() => void loadRemoteReplay(row)}
        >
          {replayLoadingId === row.matchId ? "读取中…" : "查看参与者回放"}
        </button>
      ),
    },
  ] as const;
  const localColumns = [
    {
      key: "time",
      title: "时间",
      render: (row: MatchRecord) => formatMatchTime(row.endedAt),
    },
    {
      key: "mode",
      title: "来源",
      render: (row: MatchRecord) => `本机${row.mode}`,
    },
    { key: "result", title: "结果", render: (row: MatchRecord) => row.result },
    {
      key: "placement",
      title: "名次",
      render: (row: MatchRecord) => placementLabel(row.placement),
    },
    {
      key: "score",
      title: "本机贡献",
      render: (row: MatchRecord) => row.score,
    },
    {
      key: "action",
      title: "回放",
      render: (row: MatchRecord) => (
        <button
          className="table-link"
          type="button"
          onClick={() => setReplay(localReplayPresentation(row))}
        >
          查看本机事件
        </button>
      ),
    },
  ] as const;

  return (
    <main className="sub-screen">
      <Header title="战绩与回放" subtitle="每一局都有据可查" onBack={onBack} />
      {remoteState.status === "ready" ? (
        <>
          <div className="stat-strip">
            <GlassPanel>
              <strong>{remoteSummary?.games ?? 0}</strong>
              <span>联网总局数</span>
            </GlassPanel>
            <GlassPanel>
              <strong>{percent(wins, remoteMatches.length)}</strong>
              <span>近期胜率</span>
            </GlassPanel>
            <GlassPanel>
              <strong>{percent(firstFinishes, remoteMatches.length)}</strong>
              <span>头游率</span>
            </GlassPanel>
            <GlassPanel>
              <strong>{percent(doubleUps, remoteMatches.length)}</strong>
              <span>双上率</span>
            </GlassPanel>
            <GlassPanel>
              <strong>{percent(doubleDowns, remoteMatches.length)}</strong>
              <span>双下率</span>
            </GlassPanel>
            <GlassPanel>
              <strong>
                {averagePlacement ? averagePlacement.toFixed(2) : "—"}
              </strong>
              <span>平均名次</span>
            </GlassPanel>
          </div>
          {remoteSummary ? (
            <>
              <div className="stat-strip" aria-label="权威段位与行为汇总">
                <GlassPanel>
                  <strong>{remoteSummary.currentRank}</strong>
                  <span>当前段位 · {remoteSummary.currentRating}</span>
                </GlassPanel>
                <GlassPanel>
                  <strong>{remoteSummary.highestRank}</strong>
                  <span>最高段位 · {remoteSummary.peakRating}</span>
                </GlassPanel>
                <GlassPanel>
                  <strong>
                    {remoteSummary.metrics.plays} /{" "}
                    {remoteSummary.metrics.bombsPlayed}
                  </strong>
                  <span>累计出牌 / 炸弹</span>
                </GlassPanel>
                <GlassPanel>
                  <strong>{remoteSummary.metrics.trickRights}</strong>
                  <span>累计获得牌权</span>
                </GlassPanel>
                <GlassPanel>
                  <strong>
                    {percent(behaviorRates.trusteeGames, behaviorRates.games)}
                  </strong>
                  <span>
                    近期托管局率 · {remoteSummary.metrics.managedTurns} 次
                  </span>
                </GlassPanel>
                <GlassPanel>
                  <strong>
                    {percent(
                      behaviorRates.disconnectedGames,
                      behaviorRates.games,
                    )}
                  </strong>
                  <span>
                    近期断线局率 · {remoteSummary.metrics.disconnects} 次
                  </span>
                </GlassPanel>
              </div>
              <GlassPanel className="feature-panel">
                <h2>近期积分趋势</h2>
                <p className="local-service-note">
                  {trendLabel(remoteSummary.recentTrend)}
                </p>
              </GlassPanel>
            </>
          ) : (
            <GlassPanel className="feature-panel">
              <div className="empty-state">
                <h2>暂无段位与趋势</h2>
                <p>
                  完成首局服务器最终结算后，将显示当前段位、最高段位和近期积分变化。
                </p>
              </div>
            </GlassPanel>
          )}
        </>
      ) : null}
      <GlassPanel className="feature-panel">
        <h2>联网权威战绩</h2>
        <p className="local-service-note" role="status">
          {remoteState.message}
        </p>
        {replayError ? <p role="alert">{replayError}</p> : null}
        {remoteState.status === "ready" && remoteMatches.length ? (
          <DataTable
            label="联网权威战绩列表"
            columns={remoteColumns}
            rows={remoteMatches}
            getKey={(row) => row.matchId}
          />
        ) : remoteState.status === "ready" ? (
          <div className="empty-state">
            <h3>暂无联网战绩</h3>
            <p>
              完成真人快速匹配或好友房并由服务器最终结算后，记录才会出现在这里。
            </p>
          </div>
        ) : remoteState.status === "error" ? (
          <ActionButton
            tone="blue"
            onClick={() => setRetrySequence((value) => value + 1)}
          >
            重试同步
          </ActionButton>
        ) : null}
      </GlassPanel>
      <GlassPanel className="feature-panel">
        <h2>本机权威牌局缓存</h2>
        <p className="local-service-note">
          本机好友房、AI
          补位房和离线牌局记录只保存在当前设备，不计入联网战绩、段位或排行榜。
        </p>
        {localCachedMatches.length ? (
          <DataTable
            label="本机权威牌局缓存"
            columns={localColumns}
            rows={localCachedMatches}
            getKey={(row) => row.id}
          />
        ) : (
          <div className="empty-state">
            <h3>暂无本机牌局记录</h3>
            <p>完成本机好友房或 AI 补位牌局后，权威事件会保存在当前设备。</p>
            {remoteState.status === "ready" && remoteMatches.length ? null : (
              <ActionButton tone="orange" onClick={onBack}>
                开始第一局
              </ActionButton>
            )}
          </div>
        )}
      </GlassPanel>
      <BottomNav active="records" onNavigate={onNavigate} />
      {replay ? (
        <ReplayPlayer
          replay={replay}
          onClose={() => setReplay(null)}
          onReport={
            client && replay.source === "remote" && replay.matchId
              ? (targetSeat, reason, details) =>
                  client.submitReport(
                    replay.matchId ?? "",
                    targetSeat,
                    reason,
                    details,
                  )
              : undefined
          }
        />
      ) : null}
    </main>
  );
}

type RemoteHistoryState =
  | {
      readonly status: "loading" | "unavailable" | "error";
      readonly message: string;
    }
  | {
      readonly status: "ready";
      readonly message: string;
      readonly matches: readonly AuthoritativeMatchHistory[];
      readonly summary: AuthoritativeHistorySummary | null;
    };

function ReplayPlayer({
  replay,
  onClose,
  onReport,
}: {
  replay: ReplayPresentation;
  onClose: () => void;
  onReport:
    | ((
        targetSeat: 0 | 1 | 2 | 3,
        reason: ReportReason,
        details: string,
      ) => Promise<void>)
    | undefined;
}) {
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<"0.5" | "1" | "2">("1");
  const entries = useMemo(
    () =>
      replay.authorityEvents.length
        ? replay.authorityEvents.map((event) => ({
            label: describeAuthorityEvent(event.type, event.payload, replay),
            at: event.createdAt,
          }))
        : replay.fallbackEvents.map((label, index) => ({
            label,
            at: replay.endedAt + index,
          })),
    [replay],
  );
  const roundMarkers = useMemo(
    () =>
      replay.authorityEvents.flatMap((event, index) => {
        if (event.type !== "ROUND_STARTED" && event.type !== "CARDS_DEALT")
          return [];
        const round = numberValue(event.payload.roundNumber);
        return [
          {
            index,
            roundNumber: round ?? 1,
            label: round === null ? "新一局" : `第 ${round} 局`,
          },
        ];
      }),
    [replay.authorityEvents],
  );
  const displayedRound = activeRoundNumber(roundMarkers, cursor);
  const activeRevealedHands =
    replay.revealedHandsByRound?.[String(displayedRound)];
  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(
      () =>
        setCursor((value) => {
          if (value >= entries.length - 1) {
            setPlaying(false);
            return value;
          }
          return value + 1;
        }),
      900 / Number(speed),
    );
    return () => window.clearInterval(timer);
  }, [entries.length, playing, speed]);
  return (
    <ModalSheet
      title={
        replay.source === "remote" ? "联网参与者权威回放" : "本机牌局事件回放"
      }
      onClose={onClose}
    >
      <div className="replay-stage">
        <p className="local-service-note">
          {replay.source === "remote"
            ? "来自服务器最终结算；玩家名称均为牌局封存的真实座位元数据。"
            : "来自当前设备的本机权威牌局缓存，不计入联网战绩。"}
        </p>
        {replay.checksum ? <span>事件校验摘要：{replay.checksum}</span> : null}
        <span>
          {Math.min(cursor + 1, entries.length)}/{entries.length}
        </span>
        <h3>{entries[cursor]?.label ?? "牌局已完成"}</h3>
        <input
          aria-label="回放进度"
          type="range"
          min={0}
          max={Math.max(0, entries.length - 1)}
          value={Math.min(cursor, Math.max(0, entries.length - 1))}
          onChange={(event) => setCursor(Number(event.target.value))}
        />
        <div className="button-row">
          <ActionButton
            tone="blue"
            disabled={cursor === 0}
            onClick={() => setCursor((value) => Math.max(0, value - 1))}
          >
            上一步
          </ActionButton>
          <ActionButton
            tone="orange"
            disabled={entries.length < 2}
            onClick={() => setPlaying((value) => !value)}
          >
            {playing ? "暂停" : "播放"}
          </ActionButton>
          <ActionButton
            tone="blue"
            disabled={entries.length === 0 || cursor >= entries.length - 1}
            onClick={() =>
              setCursor((value) =>
                Math.min(Math.max(0, entries.length - 1), value + 1),
              )
            }
          >
            下一步
          </ActionButton>
        </div>
        <div className="button-row">
          <SegmentedControl
            label="回放速度"
            options={[
              { value: "0.5", label: "0.5x" },
              { value: "1", label: "1x" },
              { value: "2", label: "2x" },
            ]}
            value={speed}
            onChange={setSpeed}
          />
          {roundMarkers.length ? (
            <label className="field">
              <span>跳转轮次</span>
              <select
                value={activeRound(roundMarkers, cursor)}
                onChange={(event) => setCursor(Number(event.target.value))}
              >
                {roundMarkers.map((marker) => (
                  <option key={marker.index} value={marker.index}>
                    {marker.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        {activeRevealedHands ? (
          <details>
            <summary>查看第 {displayedRound} 轮四家完整手牌</summary>
            <ol className="settlement-list">
              {Object.entries(activeRevealedHands)
                .sort(([left], [right]) => Number(left) - Number(right))
                .map(([seat, cards]) => (
                  <li key={seat}>
                    <span>
                      {replay.participants.find(
                        (player) => player.seat === Number(seat),
                      )?.nickname ?? `${Number(seat) + 1} 号位`}
                    </span>
                    <b>{cards.map(formatCard).join(" ") || "结算时已出完"}</b>
                  </li>
                ))}
            </ol>
          </details>
        ) : (
          <p>
            {replay.source === "remote"
              ? `服务器没有返回第 ${displayedRound} 轮结算手牌，当前只展示已校验的公开权威事件。`
              : `当前本机缓存没有第 ${displayedRound} 轮结算手牌。`}
          </p>
        )}
        <ReplayReportPanel replay={replay} onReport={onReport} />
      </div>
    </ModalSheet>
  );
}

function ReplayReportPanel({
  replay,
  onReport,
}: {
  replay: ReplayPresentation;
  onReport:
    | ((
        targetSeat: 0 | 1 | 2 | 3,
        reason: ReportReason,
        details: string,
      ) => Promise<void>)
    | undefined;
}) {
  const [targetSeat, setTargetSeat] = useState<0 | 1 | 2 | 3 | null>(null);
  const [reason, setReason] = useState<ReportReason>(reportReasons[0]);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  if (
    replay.source !== "remote" ||
    !replay.matchId ||
    replay.selfSeat === undefined ||
    !onReport
  )
    return null;
  const submitReport = onReport;
  const eligible = replay.participants.filter(
    (participant) => !participant.isBot && participant.seat !== replay.selfSeat,
  );
  if (!eligible.length) return null;
  const target =
    targetSeat === null
      ? null
      : (eligible.find((participant) => participant.seat === targetSeat) ??
        null);

  async function submit() {
    if (!target || busy) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await submitReport(target.seat, reason, details);
      setMessage(
        "举报已提交，管理员可在审计后台复核。重复提交不会生成多条记录。",
      );
      setTargetSeat(null);
      setDetails("");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "举报提交失败，请稍后重试",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <details>
      <summary>牌桌参与者与举报</summary>
      <ol className="settlement-list">
        {eligible.map((participant) => (
          <li key={participant.seat}>
            <span>{participant.nickname}</span>
            <button
              className="table-link"
              type="button"
              onClick={() => {
                setTargetSeat(participant.seat);
                setMessage("");
                setError("");
              }}
            >
              举报该玩家
            </button>
          </li>
        ))}
      </ol>
      {target ? (
        <div
          className="report-form"
          role="group"
          aria-label={`举报 ${target.nickname}`}
        >
          <label className="field">
            <span>举报原因</span>
            <select
              value={reason}
              onChange={(event) =>
                setReason(event.target.value as ReportReason)
              }
            >
              {reportReasons.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>补充说明（可选，最多 500 字）</span>
            <textarea
              maxLength={500}
              rows={3}
              value={details}
              onChange={(event) => setDetails(event.target.value)}
            />
          </label>
          <div className="button-row">
            <ActionButton
              tone="orange"
              disabled={busy}
              onClick={() => void submit()}
            >
              {busy ? "正在提交…" : "提交举报"}
            </ActionButton>
            <ActionButton
              tone="ghost"
              disabled={busy}
              onClick={() => {
                setTargetSeat(null);
                setDetails("");
              }}
            >
              取消
            </ActionButton>
          </div>
        </div>
      ) : null}
      {message ? <p role="status">{message}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
    </details>
  );
}

interface RankingRow {
  readonly rank: number;
  readonly name: string;
  readonly score: number;
  readonly self: boolean;
  readonly games: number;
}

export function RankingsScreen({
  profile,
  onBack,
}: {
  profile: PlayerProfile;
  onBack: () => void;
}) {
  const [scope, setScope] = useState<"season" | "reputation">("season");
  const [remoteRows, setRemoteRows] = useState<readonly RankingRow[] | null>(
    null,
  );
  const [status, setStatus] = useState("");
  const tabs = [
    { value: "season", label: "赛季榜" },
    { value: "reputation", label: "信誉榜" },
  ] as const;

  useEffect(() => {
    const api = import.meta.env.VITE_GAME_API_URL as string | undefined;
    const token = getSessionToken();
    if (!api || !token) {
      setRemoteRows([]);
      setStatus("未连接权威排行榜；本机练习不参与排名");
      return;
    }
    const controller = new AbortController();
    setRemoteRows(null);
    setStatus("正在同步权威排行榜…");
    void fetch(`${api.replace(/\/$/u, "")}/leaderboard?scope=${scope}`, {
      headers: { authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("排行榜服务暂时不可用");
        const rows = parseRankingRows(
          await response.json(),
          profile.nickname,
          scope,
        );
        setRemoteRows(rows);
        setStatus("联网权威排行榜 · 仅包含已结算真人竞技对局");
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) {
          setRemoteRows([]);
          setStatus(cause instanceof Error ? cause.message : "排行榜同步失败");
        }
      });
    return () => controller.abort();
  }, [profile.nickname, scope]);

  const rows = remoteRows ?? [];
  return (
    <main className="sub-screen">
      <Header title="排行榜" subtitle="和牌友一起进步" onBack={onBack} />
      <GlassPanel className="feature-panel">
        <p className="local-service-note">{status}</p>
        <SegmentedControl
          label="排行榜范围"
          options={tabs}
          value={scope}
          onChange={setScope}
        />
        {rows.length ? (
          <ol className="ranking-list">
            {rows.map((row) => (
              <li
                key={`${row.rank}-${row.name}`}
                className={row.self ? "is-self" : ""}
              >
                <b>{row.rank}</b>
                <span>{row.name.slice(0, 1)}</span>
                <strong>
                  {row.name}
                  <small> · {row.games} 局</small>
                </strong>
                <em>{row.score}</em>
              </li>
            ))}
          </ol>
        ) : (
          <div className="empty-state">
            <h2>暂无上榜记录</h2>
            <p>完成真人快速匹配或好友房后，权威结算会更新排行榜。</p>
          </div>
        )}
      </GlassPanel>
    </main>
  );
}

export function SettingsScreen({
  profile,
  settings,
  onChange,
  onBack,
  onNavigate,
  onLogout,
}: {
  profile: PlayerProfile;
  settings: AppSettings;
  onChange: (settings: AppSettings) => void;
  onBack: () => void;
  onNavigate: (route: LobbyRoute) => void;
  onLogout: () => void | Promise<void>;
}) {
  const [installAvailable, setInstallAvailable] = useState(false);
  useEffect(
    () => watchInstallPrompt((event) => setInstallAvailable(Boolean(event))),
    [],
  );
  const patch = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) =>
    onChange({ ...settings, [key]: value });
  const chooseBuiltIn = (value: AppSettings["musicTrack"]) =>
    onChange({
      ...settings,
      musicTrack: value,
      customMusicTrackId: null,
      customMusicTrackName: null,
    });
  return (
    <main className="sub-screen">
      <Header
        title="设置"
        subtitle={`${profile.nickname} · 账号设置与云端音乐`}
        onBack={onBack}
      />
      <GlassPanel className="settings-panel">
        <section>
          <h2>显示与牌桌</h2>
          <label className="settings-row">
            <span>
              <b>文字缩放</b>
              <small>支持最高 200%</small>
            </span>
            <select
              value={settings.textScale}
              onChange={(event) =>
                patch(
                  "textScale",
                  Number(event.target.value) as AppSettings["textScale"],
                )
              }
            >
              <option value={1}>100%</option>
              <option value={1.25}>125%</option>
              <option value={1.5}>150%</option>
              <option value={2}>200%</option>
            </select>
          </label>
          <label className="settings-row">
            <span>
              <b>牌张尺寸</b>
              <small>牌局中也可快速切换</small>
            </span>
            <select
              value={settings.cardSize}
              onChange={(event) =>
                patch("cardSize", event.target.value as AppSettings["cardSize"])
              }
            >
              <option value="small">小</option>
              <option value="medium">中</option>
              <option value="large">大</option>
            </select>
          </label>
        </section>
        <section>
          <h2>声音与音乐</h2>
          <ToggleRow
            label="背景音乐"
            note="可使用内置曲目或自己的云端音乐"
            checked={settings.music}
            onChange={(value) => patch("music", value)}
          />
          <label className="settings-row">
            <span>
              <b>内置背景曲目</b>
              <small>
                {settings.customMusicTrackName
                  ? `当前使用：${settings.customMusicTrackName}`
                  : MUSIC_TRACKS.find(
                      (track) => track.id === settings.musicTrack,
                    )?.description}
              </small>
            </span>
            <select
              aria-label="背景曲目"
              value={
                settings.customMusicTrackId ? "custom" : settings.musicTrack
              }
              disabled={!settings.music}
              onChange={(event) => {
                if (event.target.value !== "custom")
                  chooseBuiltIn(
                    event.target.value as AppSettings["musicTrack"],
                  );
              }}
            >
              <option value="custom" disabled>
                我的云端音乐
              </option>
              {MUSIC_TRACKS.map((track) => (
                <option value={track.id} key={track.id}>
                  {track.label}
                </option>
              ))}
            </select>
          </label>
          <label className="settings-row">
            <span>
              <b>音乐音量</b>
              <small>{Math.round(settings.musicVolume * 100)}%</small>
            </span>
            <input
              aria-label="音乐音量"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={settings.musicVolume}
              disabled={!settings.music}
              onChange={(event) =>
                patch("musicVolume", Number(event.target.value))
              }
            />
          </label>
          <MusicLibraryPanel settings={settings} onChange={onChange} />
          <ToggleRow
            label="出牌音效"
            note="出牌、不出、轮次、炸弹与结算提示"
            checked={settings.soundEffects}
            onChange={(value) => patch("soundEffects", value)}
          />
          <label className="settings-row">
            <span>
              <b>音效音量</b>
              <small>{Math.round(settings.soundEffectsVolume * 100)}%</small>
            </span>
            <input
              aria-label="音效音量"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={settings.soundEffectsVolume}
              disabled={!settings.soundEffects}
              onChange={(event) =>
                patch("soundEffectsVolume", Number(event.target.value))
              }
            />
          </label>
        </section>
        <section>
          <h2>触感</h2>
          <ToggleRow
            label="触感反馈"
            note="锁牌和轮到你时振动"
            checked={settings.vibration}
            onChange={(value) => patch("vibration", value)}
          />
        </section>
        <section>
          <h2>PWA 与连接</h2>
          <p className="connection-status">
            <span className={navigator.onLine ? "online" : "offline"} />
            {navigator.onLine ? "网络已连接" : "当前离线，已启用离线壳"}
          </p>
          <ActionButton
            tone="blue"
            disabled={!installAvailable}
            onClick={() => void promptPwaInstall()}
          >
            安装到此设备
          </ActionButton>
          <small>
            {installAvailable
              ? "可安装为独立应用"
              : "已安装，或当前浏览器不提供安装提示"}
          </small>
          <ActionButton tone="ghost" onClick={() => void onLogout()}>
            退出登录
          </ActionButton>
        </section>
      </GlassPanel>
      <BottomNav active="settings" onNavigate={onNavigate} />
    </main>
  );
}

function MusicLibraryPanel({
  settings,
  onChange,
}: {
  settings: AppSettings;
  onChange: (settings: AppSettings) => void;
}) {
  const [tracks, setTracks] = useState<readonly UserMusicTrack[]>([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("正在同步云端音乐…");
  const player = useRef<UserMusicPlayer | null>(null);
  if (!player.current) player.current = new UserMusicPlayer();
  useEffect(() => {
    let active = true;
    void userMusicApi
      .list()
      .then((items) => {
        if (active) {
          setTracks(items);
          setStatus(
            items.length
              ? `已保存 ${items.length} 首，仅当前账号可见`
              : "还没有上传音乐",
          );
        }
      })
      .catch((cause: unknown) => {
        if (active)
          setStatus(cause instanceof Error ? cause.message : "音乐库同步失败");
      });
    return () => {
      active = false;
      player.current?.dispose();
      player.current = null;
    };
  }, []);

  async function upload(file: File | undefined) {
    if (!file || busy) return;
    if (file.size > 20 * 1024 * 1024) {
      setStatus("音乐文件不能超过 20 MB");
      return;
    }
    setBusy(true);
    setStatus("正在安全上传到专用 R2 音乐库…");
    try {
      const track = await userMusicApi.upload(file);
      setTracks((items) => [track, ...items]);
      onChange({
        ...settings,
        music: true,
        customMusicTrackId: track.id,
        customMusicTrackName: track.filename,
      });
      setStatus(`已上传并选用：${track.filename}`);
    } catch (cause) {
      setStatus(cause instanceof Error ? cause.message : "上传失败");
    } finally {
      setBusy(false);
    }
  }

  async function preview(track: UserMusicTrack) {
    setStatus(`正在加载：${track.filename}`);
    try {
      await player.current?.prepare(track.id);
      await player.current?.play(settings.musicVolume);
      setStatus(`正在试听：${track.filename}`);
    } catch (cause) {
      setStatus(cause instanceof Error ? cause.message : "无法试听");
    }
  }

  async function remove(track: UserMusicTrack) {
    if (busy) return;
    setBusy(true);
    try {
      await userMusicApi.remove(track.id);
      setTracks((items) => items.filter((item) => item.id !== track.id));
      if (settings.customMusicTrackId === track.id)
        onChange({
          ...settings,
          customMusicTrackId: null,
          customMusicTrackName: null,
        });
      setStatus(`已删除：${track.filename}`);
    } catch (cause) {
      setStatus(cause instanceof Error ? cause.message : "删除失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="music-library">
      <div className="music-library-head">
        <span>
          <b>我的云端音乐</b>
          <small>MP3、M4A、OGG、WAV 或 WebM，单首不超过 20 MB</small>
        </span>
        <label className={`music-upload ${busy ? "is-disabled" : ""}`}>
          <input
            type="file"
            accept="audio/mpeg,audio/mp4,audio/ogg,audio/wav,audio/webm"
            disabled={busy}
            onChange={(event) => {
              void upload(event.target.files?.[0]);
              event.currentTarget.value = "";
            }}
          />
          {busy ? "处理中…" : "上传音乐"}
        </label>
      </div>
      <p className="music-library-status" role="status">
        {status}
      </p>
      {tracks.length ? (
        <ul className="music-track-list">
          {tracks.map((track) => (
            <li
              key={track.id}
              className={
                settings.customMusicTrackId === track.id ? "is-selected" : ""
              }
            >
              <div>
                <b>{track.filename}</b>
                <small>{(track.sizeBytes / 1024 / 1024).toFixed(1)} MB</small>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...settings,
                      music: true,
                      customMusicTrackId: track.id,
                      customMusicTrackName: track.filename,
                    })
                  }
                >
                  {settings.customMusicTrackId === track.id ? "使用中" : "选用"}
                </button>
                <button type="button" onClick={() => void preview(track)}>
                  试听
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={busy}
                  onClick={() => void remove(track)}
                >
                  删除
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ToggleRow({
  label,
  note,
  checked,
  onChange,
}: {
  label: string;
  note: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="settings-row">
      <span>
        <b>{label}</b>
        <small>{note}</small>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

export function SettlementScreen({
  record,
  againLabel,
  againBusy = false,
  againError = "",
  onReplay,
  onAgain,
  onLobby,
}: {
  record: MatchRecord;
  againLabel: string;
  againBusy?: boolean;
  againError?: string;
  onReplay: () => void;
  onAgain: () => void;
  onLobby: () => void;
}) {
  const winningTeam =
    record.winningTeam ??
    record.participants.find((item) => item.finishOrder === 1)?.team;
  const fixedRounds =
    record.outcomeBasis !== undefined && record.outcomeBasis !== "through-ace";
  return (
    <main className="settlement-screen">
      <GlassPanel>
        <div className="settlement-title">
          <span>{record.result === "胜利" ? "✓" : "·"}</span>
          <div>
            <h1>
              我方{record.result} · {record.teamResult}
            </h1>
            <p>
              {record.result === "胜利"
                ? "权威结算已确认本方胜出"
                : "本局名次与公开事件已写入战绩"}
            </p>
          </div>
        </div>
        <div className="settlement-grid">
          <article>
            <span>名次</span>
            <strong>{placementLabel(record.placement)}</strong>
          </article>
          <article>
            <span>{fixedRounds ? "胜方终局级别" : "级牌变化"}</span>
            <strong>
              {fixedRounds
                ? record.levelAfter
                : record.result === "胜利"
                  ? `${record.levelBefore} → ${record.levelAfter}`
                  : "本方未升级"}
            </strong>
          </article>
          <article>
            <span>团队贡献</span>
            <strong>{record.score}</strong>
          </article>
          <article>
            <span>经验</span>
            <strong>+{record.experienceDelta}</strong>
          </article>
          <article>
            <span>信誉</span>
            <strong>{signed(record.reputationDelta)}</strong>
          </article>
          <article>
            <span>出牌 / 炸弹</span>
            <strong>
              {record.metrics.plays} / {record.metrics.bombs}
            </strong>
          </article>
          <article>
            <span>获得牌权</span>
            <strong>{record.metrics.trickRights}</strong>
          </article>
          <article>
            <span>让牌成功</span>
            <strong>{record.metrics.teamAssists}</strong>
          </article>
          <article>
            <span>托管时长</span>
            <strong>{record.metrics.trusteeSeconds} 秒</strong>
          </article>
          <article>
            <span>断线次数</span>
            <strong>{record.metrics.disconnects}</strong>
          </article>
        </div>
        <ol className="settlement-list">
          {[...record.participants]
            .sort((left, right) => left.finishOrder - right.finishOrder)
            .map((player) => (
              <li key={player.seat}>
                <span>
                  {player.nickname}
                  {player.isBot ? " · AI" : ""}
                </span>
                <b>
                  {placementLabel(player.finishOrder)} ·{" "}
                  {player.team === winningTeam ? "胜方" : "负方"}
                </b>
              </li>
            ))}
        </ol>
        {againError ? (
          <p className="form-error" role="alert">
            {againError}
          </p>
        ) : null}
        <div className="button-row">
          <ActionButton tone="orange" disabled={againBusy} onClick={onAgain}>
            {againBusy ? "正在创建续局…" : againLabel}
          </ActionButton>
          <ActionButton tone="blue" disabled={againBusy} onClick={onReplay}>
            查看回放
          </ActionButton>
          <ActionButton tone="ghost" disabled={againBusy} onClick={onLobby}>
            返回大厅
          </ActionButton>
        </div>
      </GlassPanel>
    </main>
  );
}

function placementLabel(value: 1 | 2 | 3 | 4): string {
  return (["头游", "二游", "三游", "末游"] as const)[value - 1] ?? "名次待同步";
}
function percent(value: number, total: number): string {
  return total ? `${Math.round((value / total) * 100)}%` : "—";
}
function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}
function trendLabel(values: readonly number[]): string {
  return values.length
    ? values
        .map((value) =>
          value > 0 ? `↑ +${value}` : value < 0 ? `↓ ${value}` : "→ 0",
        )
        .join(" · ")
    : "暂无近期走势";
}
function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
function recordValue(value: unknown): Readonly<Record<string, unknown>> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;
}

function activeRound(
  markers: readonly { index: number }[],
  cursor: number,
): number {
  let active = markers[0]?.index ?? 0;
  for (const marker of markers)
    if (marker.index <= cursor) active = marker.index;
  return active;
}

function activeRoundNumber(
  markers: readonly { index: number; roundNumber: number }[],
  cursor: number,
): number {
  let active = 1;
  for (const marker of markers)
    if (marker.index <= cursor) active = marker.roundNumber;
  return active;
}

function formatCard(id: string): string {
  try {
    const card = cardFromId(id);
    return `${card.suit}${card.rank}`;
  } catch {
    return id;
  }
}

function formatMatchTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function localReplayPresentation(record: MatchRecord): ReplayPresentation {
  return {
    source: "local-practice",
    endedAt: record.endedAt,
    participants: record.participants,
    authorityEvents: record.authorityEvents,
    fallbackEvents: record.events,
    ...(record.revealedHandsByRound
      ? { revealedHandsByRound: record.revealedHandsByRound }
      : record.revealedHands
        ? {
            revealedHandsByRound: {
              [String(activeRoundNumberFromEvents(record.authorityEvents))]:
                record.revealedHands,
            },
          }
        : {}),
  };
}

function activeRoundNumberFromEvents(
  events: readonly {
    readonly type: string;
    readonly payload: Readonly<Record<string, unknown>>;
  }[],
): number {
  let latest = 1;
  for (const event of events) {
    if (event.type !== "ROUND_STARTED" && event.type !== "CARDS_DEALT")
      continue;
    const roundNumber = numberValue(event.payload.roundNumber);
    if (
      roundNumber !== null &&
      Number.isInteger(roundNumber) &&
      roundNumber > latest
    )
      latest = roundNumber;
  }
  return latest;
}

function describeAuthorityEvent(
  type: string,
  payload: Readonly<Record<string, unknown>>,
  replay: ReplayPresentation,
): string {
  const seat = numberValue(payload.seat);
  const player =
    seat === null
      ? null
      : replay.participants.find((item) => item.seat === seat);
  const name =
    player?.nickname ?? (seat === null ? "牌桌" : `${seat + 1} 号位`);
  if (type === "CARDS_PLAYED") {
    const cards = Array.isArray(payload.cardIds) ? payload.cardIds.length : 0;
    const combination = recordValue(payload.combination);
    const kind =
      stringValue(combination?.kind) ??
      stringValue(combination?.normalizationKey) ??
      "合法牌型";
    return `${name} 打出 ${cards} 张牌 · ${kind}`;
  }
  if (type === "PLAYER_PASSED") return `${name} 选择不出`;
  if (type === "PLAYER_FINISHED")
    return `${name} 完成手牌 · ${placementLabel((numberValue(payload.order) ?? 4) as 1 | 2 | 3 | 4)}`;
  if (type === "TRICK_RESET") return `${name} 获得下一轮牌权`;
  if (type === "ROUND_SETTLED") return "本轮权威结算完成";
  if (type === "LEVEL_ADVANCED") {
    const before = stringValue(payload.levelBefore);
    const after = stringValue(payload.levelAfter);
    const advancement = numberValue(payload.advancement);
    if (before && after) return `胜方从 ${before} 升至 ${after}`;
    return advancement === null
      ? "胜方等级变更已由服务器确认"
      : `胜方升级 ${advancement} 级`;
  }
  if (type === "ACE_RESET") return "连续三把未过 A，等级退回 2";
  if (type === "TRIBUTE_OFFERED") return `${name} 完成进贡`;
  if (type === "TRIBUTE_RETURNED") return `${name} 完成还贡`;
  if (type === "ANTI_TRIBUTE") return "本轮触发抗贡";
  if (type === "CONTROL_CHANGED")
    return `${name} 控制状态变更为 ${stringValue(payload.control) ?? "已同步"}`;
  if (type === "MATCH_SETTLED") return "整局最终结算并封存事件链";
  if (type === "ROUND_STARTED" || type === "CARDS_DEALT") {
    const round = numberValue(payload.roundNumber);
    return round === null ? "新一局开始" : `第 ${round} 局开始`;
  }
  return type;
}

function parseRankingRows(
  value: unknown,
  selfNickname: string,
  scope: "season" | "reputation",
): readonly RankingRow[] {
  const envelope = recordValue(value);
  if (!envelope || !Array.isArray(envelope.entries))
    throw new Error("排行榜响应格式无效");
  return envelope.entries.map((entry, index) => {
    const row = recordValue(entry);
    const name = stringValue(row?.nickname);
    const rank = numberValue(row?.rank);
    const games = numberValue(row?.games);
    const rating = numberValue(
      scope === "reputation" ? row?.reputation : row?.rating,
    );
    if (!row || !name || rank === null || games === null || rating === null)
      throw new Error("排行榜条目格式无效");
    return {
      rank: Math.max(1, Math.trunc(rank || index + 1)),
      name,
      score: Math.trunc(rating),
      self: name === selfNickname,
      games: Math.max(0, Math.trunc(games)),
    };
  });
}
